import axios, { AxiosError, AxiosResponse, InternalAxiosRequestConfig } from 'axios';
import { configService } from './ConfigService';
import { isViteDev } from '../config';

export interface ApiError {
  statusCode: number;
  message: string;
  details: string;
  timestamp: string;
}

export interface RetryConfig {
  retries: number;
  retryDelay: number;
  retryCondition?: (error: AxiosError) => boolean;
}

interface RequestMetadata {
  retryConfig: RetryConfig;
  retryCount: number;
}

interface ExtendedAxiosRequestConfig extends InternalAxiosRequestConfig {
  metadata?: RequestMetadata;
  retryConfig?: Partial<RetryConfig>;
}

interface ServerErrorResponse {
  statusCode?: number;
  message?: string;
  // Controllers in this API return validation and conflict bodies as `{ error: "..." }`
  // (BadRequest/Conflict), not `{ message: "..." }`. Without this the actual reason — which
  // retention floor was violated, why an export was refused — was replaced by a generic string.
  error?: string;
  details?: string;
  timestamp?: string;
}

// Default retry configuration
const defaultRetryConfig: RetryConfig = {
  retries: 3,
  retryDelay: 1000,
  retryCondition: (error: AxiosError) => {
    // Retry on network errors or 5xx server errors
    return !error.response || (error.response.status >= 500 && error.response.status < 600);
  },
};

// Get configuration from centralized service
const config = configService.get();

// Create axios instance with base configuration
export const apiClient = axios.create({
  baseURL: config.apiBaseUrl,
  timeout: 30000,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Development role switcher - allows testing different roles without backend auth
if (import.meta.env.DEV) {
  const devRole = localStorage.getItem('dev_role') || 'TenantAdmin';
  const devTenant = localStorage.getItem('dev_tenant') || 'demo';
  const devUserId = localStorage.getItem('dev_user_id') || 'test-user-123';
  
  apiClient.defaults.headers.common['X-User-Role'] = devRole;
  apiClient.defaults.headers.common['X-Tenant-Id'] = devTenant;
  apiClient.defaults.headers.common['X-User-Id'] = devUserId;

  console.log(`[DEV] Role Switcher Active - Current Role: ${devRole}, Tenant: ${devTenant}, User: ${devUserId}`);
  console.log('[DEV] To switch roles, use: localStorage.setItem("dev_role", "TenantAdmin|Supervisor|Agent|PlatformAdmin"); location.reload();');
}

// Request interceptor to add auth token and request ID
apiClient.interceptors.request.use(
  async (config: ExtendedAxiosRequestConfig) => {
    // Get token from MSAL instead of localStorage
    try {
      const { authService } = await import('./authService');
      const token = await authService.getToken();
      if (token) {
        config.headers.Authorization = `Bearer ${token}`;
      }
    } catch (_error) {
      // Silent error handling for token acquisition
    }

    // Add request ID for tracking
    config.headers['X-Request-ID'] = generateRequestId();

    // Add retry configuration to request metadata
    if (!config.metadata) {
      config.metadata = {
        retryConfig: { ...defaultRetryConfig, ...config.retryConfig },
        retryCount: 0,
      };
    }

    return config;
  },
  (error: AxiosError) => {
    logError('Request interceptor error', error);
    return Promise.reject(error);
  }
);

// Response interceptor for error handling and retry logic
apiClient.interceptors.response.use(
  (response: AxiosResponse) => {
    return response;
  },
  async (error: AxiosError) => {
    const originalRequest = error.config as ExtendedAxiosRequestConfig;

    // Handle authentication errors
    if (error.response?.status === 401) {
      localStorage.removeItem('authToken');
      // In development, try to get a dummy token instead of redirecting
      if (isViteDev) {
        try {
          const { authService } = await import('./authService');
          const token = await authService.getToken();
          if (token) {
            // Retry the original request with the new token
            const retryRequest = error.config as ExtendedAxiosRequestConfig;
            retryRequest.headers.Authorization = `Bearer ${token}`;
            return apiClient.request(retryRequest);
          }
        } catch (_authError) {
          // Silent error handling
        }
      }
      return Promise.reject(createApiError(error));
    }

    // Retry logic
    const retryConfig = originalRequest?.metadata?.retryConfig || defaultRetryConfig;
    const retryCount = originalRequest?.metadata?.retryCount || 0;

    if (
      retryCount < retryConfig.retries &&
      retryConfig.retryCondition?.(error) &&
      originalRequest
    ) {
      if (originalRequest.metadata) {
        originalRequest.metadata.retryCount = retryCount + 1;
      }

      // Calculate delay with exponential backoff
      const delay = retryConfig.retryDelay * Math.pow(2, retryCount);

      logError(
        `Retrying request (attempt ${retryCount + 1}/${retryConfig.retries}) after ${delay}ms`,
        error
      );

      await new Promise((resolve) => setTimeout(resolve, delay));
      return apiClient(originalRequest);
    }

    // Log error and create user-friendly error
    const apiError = createApiError(error);
    logError('API request failed', error, apiError);

    return Promise.reject(apiError);
  }
);

// Helper function to create user-friendly API errors
function createApiError(error: AxiosError): ApiError {
  if (error.response?.data && typeof error.response.data === 'object') {
    const serverError = error.response.data as ServerErrorResponse;
    return {
      statusCode: serverError.statusCode || error.response.status,
      message: serverError.message || serverError.error || 'An error occurred',
      details: serverError.details || error.message,
      timestamp: serverError.timestamp || new Date().toISOString(),
    };
  }

  // Network or other errors
  if (error.code === 'NETWORK_ERROR' || !error.response) {
    return {
      statusCode: 0,
      message: 'Network Error',
      details:
        'Unable to connect to the server. Please check your internet connection and try again.',
      timestamp: new Date().toISOString(),
    };
  }

  // Timeout errors
  if (error.code === 'ECONNABORTED') {
    return {
      statusCode: 408,
      message: 'Request Timeout',
      details: 'The request took too long to complete. Please try again.',
      timestamp: new Date().toISOString(),
    };
  }

  // Generic error
  return {
    statusCode: error.response?.status || 500,
    message: 'An unexpected error occurred',
    details: error.message || 'Please try again or contact support if the problem persists.',
    timestamp: new Date().toISOString(),
  };
}

// Helper function to generate unique request IDs
function generateRequestId(): string {
  return `req_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
}

// Enhanced logging function
function logError(message: string, error: AxiosError, apiError?: ApiError) {
  const errorLog = {
    url: error.config?.url,
    method: error.config?.method?.toUpperCase(),
    requestId: error.config?.headers?.['X-Request-ID'],
    statusCode: error.response?.status,
    errorCode: error.code,
    apiError,
  };

  // Use the logging service for consistent error logging
  import('./loggingService').then(({ loggingService }) => {
    loggingService.error(message, errorLog, error);
  });
}

// Enhanced API client with custom retry configuration
export const createApiClientWithRetry = (retryConfig?: Partial<RetryConfig>) => {
  const config = configService.get();

  const client = axios.create({
    baseURL: config.apiBaseUrl,
    timeout: 30000,
    headers: {
      'Content-Type': 'application/json',
    },
    retryConfig: { ...defaultRetryConfig, ...retryConfig },
  } as ExtendedAxiosRequestConfig);

  // Apply the same interceptors as the main client
  client.interceptors.request.use(
    async (config) => {
      // Get token from MSAL instead of localStorage
      try {
        const { authService } = await import('./authService');
        const token = await authService.getToken();
        if (token) {
          config.headers.Authorization = `Bearer ${token}`;
        }
      } catch (_error) {
        // Silent error handling for token acquisition
      }
      return config;
    },
    (error) => Promise.reject(error)
  );

  client.interceptors.response.use(
    (response) => response,
    (error) => {
      if (error.response?.status === 401) {
        // Let AuthGuard handle authentication
      }
      return Promise.reject(error);
    }
  );

  return client;
};
